/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Alert,
    Button,
    Stack,
    VStack,
    Text,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon, BrandLogo} from '@salesforce/retail-react-app/app/components/icons'
import StandardLogin from '../../components/standard-login'
import PasswordlessLogin from '../../components/passwordless-login'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useLocation} from 'react-router-dom'
import {AccountDrawerLogo} from '../icons'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useAuthModal} from '../../hooks/use-auth-modal'

const LoginForm = ({
    submitForm,
    handleForgotPasswordClick,
    handlePasswordlessLoginClick,
    clickCreateAccount = noop,
    form,
    isPasswordlessEnabled = false,
    isSocialEnabled = false,
    idps = [],
    setLoginType,
    prefix = ''
}) => {
    const config = getConfig()
    const enabledIDPs = config.app.SFID_SOCIAL_LOGIN_ENABLED_IDPS
    const hasSocialOptions =
        enabledIDPs && enabledIDPs.trim() && enabledIDPs.toLowerCase() !== 'false'
    const shouldShowSocial = isSocialEnabled && hasSocialOptions && idps.length > 0
    const location = useLocation()
    const navigate = useNavigation()
    const isLoginPage = location.pathname === '/login'
    const authModal = useAuthModal()

    return (
        <Fragment>
            {/* Header - only show when NOT on login page */}
            {!isLoginPage && (
                <Stack justify="center" align="center" spacing={0} marginBottom={8}>
                    <AccountDrawerLogo width="110px" height="auto" />
                    <Text align="center" fontSize="md" fontWeight="medium">
                        <FormattedMessage
                            defaultMessage="Join, earn points, get spoiled"
                            id="login_form.message.join"
                        />
                    </Text>
                </Stack>
            )}

            <form
                id="login-form"
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-auth-modal-form"
                noValidate
            >
                {form.formState.errors?.global && (
                    <Alert
                        status="error"
                        marginBottom={4}
                        p={0}
                        backgroundColor="transparent"
                        border="none"
                    >
                        <Text fontSize="sm" ml={{base: 4, lg: 0}} color="dangerText">
                            {form.formState.errors.global.message}
                        </Text>
                    </Alert>
                )}

                <Stack spacing={6} px={0}>
                    {isPasswordlessEnabled ? (
                        <PasswordlessLogin
                            form={form}
                            handleForgotPasswordClick={handleForgotPasswordClick}
                            handlePasswordlessLoginClick={handlePasswordlessLoginClick}
                            idps={idps}
                            setLoginType={setLoginType}
                            enabledIDPs={enabledIDPs}
                            shouldShowSocial={shouldShowSocial}
                        />
                    ) : (
                        <StandardLogin
                            form={form}
                            handleForgotPasswordClick={handleForgotPasswordClick}
                            idps={idps}
                            enabledIDPs={enabledIDPs}
                            shouldShowSocial={shouldShowSocial}
                            isLoginPage={isLoginPage}
                            prefix={prefix}
                        />
                    )}

                    {/* Terms & Conditions and Privacy Policy */}
                    <Text
                        fontSize="12px"
                        textAlign="left"
                        color="gray.700"
                        fontWeight="400"
                        lineHeight="15px"
                        marginTop={enabledIDPs ? {base: '-45px', lg: '-50px'} : 0}
                        marginBottom={isLoginPage ? '58px' : 0}
                        px={isLoginPage && {base: 4, lg: 0}}
                        pr={!isLoginPage && '1px'}
                    >
                        <FormattedMessage
                            defaultMessage="By continuing, you agree to our {termsLink} and {privacyLink}."
                            id="login_form.message.terms_privacy"
                            values={{
                                termsLink: (
                                    <Link
                                        href="/terms-conditions.html"
                                        textDecoration="underline"
                                        color="gray.700"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Terms & Conditions"
                                            id="login_form.link.terms_conditions"
                                        />
                                    </Link>
                                ),
                                privacyLink: (
                                    <Link
                                        href="/privacy-policy.html"
                                        textDecoration="underline"
                                        color="gray.700"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Privacy Policy"
                                            id="login_form.link.privacy_policy"
                                        />
                                    </Link>
                                )
                            }}
                        />
                    </Text>

                    {/* Create account section - varies by page */}
                    {isLoginPage ? (
                        <VStack spacing={2} px={{base: 4, lg: 0}} justify="center">
                            <Text fontSize="sm" lineHeight="18px">
                                <FormattedMessage
                                    defaultMessage="Don't have an account?"
                                    id="login_form.message.dont_have_account"
                                />{' '}
                                <FormattedMessage
                                    defaultMessage="Create One!"
                                    id="login_page_form.action.create_account"
                                />
                            </Text>
                            <Button
                                data-testid="login-form-join-collectif-button"
                                fontSize="md"
                                fontWeight="bold"
                                variant="outline"
                                onClick={clickCreateAccount}
                                w="full"
                                textTransform="uppercase"
                            >
                                <FormattedMessage
                                    defaultMessage="Join our Collectif"
                                    id="login_join.our.collectif"
                                />
                            </Button>
                        </VStack>
                    ) : (
                        <Stack mt={4}>
                            <Stack direction="row" spacing={1}>
                                <Text fontSize="sm">
                                    <FormattedMessage
                                        defaultMessage="Don't have an account?"
                                        id="login_form.message.dont_have_account"
                                    />
                                </Text>
                                <Button
                                    data-testid="login-form-create-account-link"
                                    variant="link"
                                    size="sm"
                                    onClick={() => {
                                        navigate('/registration')
                                    }}
                                >
                                    <FormattedMessage
                                        defaultMessage="Create an account"
                                        id="login_form.action.create_account"
                                    />
                                </Button>
                            </Stack>

                            <Button
                                data-testid="login-form-join-collectif-drawer-button"
                                variant="outline"
                                color="black"
                                borderRadius={0}
                                border="1px solid"
                                borderColor="black"
                                mt={2}
                                fontSize="md"
                                onClick={() => {
                                    navigate('/registration')
                                }}
                            >
                                <FormattedMessage
                                    defaultMessage="Join Our Collectif"
                                    id="login_form.action.join.collectif"
                                />
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </form>
        </Fragment>
    )
}

LoginForm.propTypes = {
    submitForm: PropTypes.func,
    handleForgotPasswordClick: PropTypes.func,
    clickCreateAccount: PropTypes.func,
    handlePasswordlessLoginClick: PropTypes.func,
    form: PropTypes.object,
    isPasswordlessEnabled: PropTypes.bool,
    isSocialEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string),
    setLoginType: PropTypes.func,
    prefix: PropTypes.string
}

export default LoginForm
